import React from "react";
import { TbChevronRight } from "react-icons/tb";
import { useReviewOverview } from "../../hooks/useReviewSchedule";
import { Button, Alert, EmptyState, TopbarSlot } from "../../components";
import { fmtDate } from "../../lib/dates";
import { STATE_LABEL } from "../../lib/ratings";
import "./Reviews.css";

// Three metrics, recent work, next reviews. The whole screen.
//
// The kit's third metric is managed monthly revenue. Scanner organizations carry no
// revenue, so that tile would be a number with nothing behind it; reviews due takes its
// place, which is real and is what someone opens this screen to find out.
const OverviewScreen = ({ me, onNavigate, reviewOrgId }) => {
  const { metrics, recent, upcoming, loading, error } = useReviewOverview(reviewOrgId);

  const firstName = (me?.name || "").split(/[\s@.]/)[0];

  return (
    <div className="v2-page">
      <TopbarSlot>
        {/* Carries the same one-shot "new" intent as the rail flyout's New assessment,
            so the board opens with the create dialog up rather than just landing there. */}
        <Button variant="primary" onClick={() => onNavigate && onNavigate("assessments", null, { type: "new" })}>
          New assessment
        </Button>
      </TopbarSlot>

      <div className="v2-pagehead">
        <div>
          <div className="v2-eyebrow">{me?.tenant_name || "Workspace"}</div>
          <h2>{firstName ? `Welcome back, ${firstName}.` : "Business reviews"}</h2>
        </div>
      </div>

      <Alert>{error}</Alert>

      {loading ? <p className="v2-hint">Loading…</p> : (
        <>
          {/* One band divided by rules, not three separate cards. */}
          <div className="v2-metrics">
            <div className="v2-metric">
              <div className="v2-metric-label">Active assessments</div>
              <div className="v2-metric-value">{metrics?.active_assessments ?? 0}</div>
              <div className="v2-metric-sub">
                {metrics?.drafts ?? 0} drafts · {metrics?.ready ?? 0} ready
              </div>
            </div>
            {/* Dropped, not zeroed, when the area is filtered to one organization: the
                tile counts clients, and narrowed to a single client it can only ever say
                "1". The API returns null rather than a number for exactly this case. */}
            {metrics?.managed_clients != null && (
              <div className="v2-metric">
                <div className="v2-metric-label">Managed clients</div>
                <div className="v2-metric-value">{metrics.managed_clients}</div>
                <div className="v2-metric-sub">
                  {metrics.managed_clients} organizations in this workspace
                </div>
              </div>
            )}
            <div className="v2-metric">
              <div className="v2-metric-label">Reviews due</div>
              <div className="v2-metric-value" style={metrics?.reviews_due ? { color: "var(--v2-danger)" } : undefined}>
                {metrics?.reviews_due ?? 0}
              </div>
              <div className="v2-metric-sub">at or past their next review date</div>
            </div>
          </div>

          <div className="v2-reviewpanes">
            <section>
              <div className="v2-pane-head">
                <div className="v2-section-title">Recent work</div>
                <button type="button" className="v2-btn v2-btn--text"
                        onClick={() => onNavigate && onNavigate("assessments")}>View all</button>
              </div>
              {(recent || []).length === 0 ? (
                <EmptyState title="Nothing yet">No assessment has been started.</EmptyState>
              ) : (
                <div className="v2-feed">
                  {/* Each row opens that assessment in the editor, the same address the
                      Assessments board opens it at. */}
                  {recent.map((r) => (
                    <button key={r.id} type="button" className="v2-feed-row is-link"
                            onClick={() => onNavigate && onNavigate("assessments", r.id)}>
                      <span className="v2-tenant-mark">{(r.organization_name || "?").slice(0, 1).toUpperCase()}</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="v2-feed-title">{r.title}</div>
                        <div className="v2-feed-sub">
                          {r.organization_name} · {STATE_LABEL[r.state] || r.state}
                        </div>
                      </div>
                      <span className={`v2-feed-state ${r.state}`}>{r.state === "presented" ? "View" : "Continue"}</span>
                      <TbChevronRight size={15} className="v2-feed-chev" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              )}
            </section>

            <aside>
              <div className="v2-pane-head">
                <div className="v2-section-title">Business reviews</div>
                <button type="button" className="v2-btn v2-btn--text"
                        onClick={() => onNavigate && onNavigate("review-schedule")}>View schedule</button>
              </div>
              {(upcoming || []).length === 0 ? (
                <p className="v2-hint">No cadence has been set yet.</p>
              ) : (
                <div className="v2-feed">
                  {upcoming.map((u) => (
                    <div key={u.organization_id} className="v2-feed-row">
                      <span className="v2-feed-dot"
                            style={u.overdue ? { background: "var(--v2-danger)" } : undefined} />
                      <div style={{ minWidth: 0, display: "flex", width: "100%", gap: ".5rem" }}>
                        <span className="v2-feed-title" style={{ flex: 1 }}>{u.organization_name}</span>
                        <span className="v2-feed-sub" style={u.overdue ? { color: "var(--v2-danger)" } : undefined}>
                          {u.overdue ? "Overdue" : fmtDate(u.next_review)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
};

export default OverviewScreen;
